/**
 * core/rbac.ts · 角色×页面×字段权限矩阵
 *
 * 复用 v17.0.215 TN_FIELD_ACL 设计 (3 表 · 30 字段 SEED · 4 敏感度):
 *   - field_acl_definitions (字段定义)
 *   - field_acl_rules (role × field 矩阵)
 *   - field_acl_log (审计)
 *
 * v300 简化为:
 *   - 页面级 RBAC (本文件 · 静态白名单)
 *   - 字段级 RBAC 走 services/ 层动态查 field_acl_rules 表 (团队 W3 补)
 *
 * 设计原则:
 *   - admin / gm / boss = 全可见 (_all: true)
 *   - 其他角色按白名单 · 不在白名单 = 拒绝
 *   - canAccess() 是核心 API · router.ts 调
 */

import type { User } from './auth';

interface RolePermission {
  _all?: boolean;
  pages?: string[];
}

const ROLE_PERMISSIONS: Record<string, RolePermission> = {
  admin: { _all: true },
  gm: { _all: true },
  boss: { _all: true },

  sales: {
    // 销售收窄: 不开放 AR 发票 / AR 账龄 (财务入口, sales 仅能看自己客户的销售订单)
    // arInvoiceList / arAging 改走 FIN / GM / boss 角色
    pages: [
      'salesHome',
      'quotationCreate', 'quotationList', 'quotationDetail',
      'salesOrderList', 'salesOrderDetail', 'order360',
      'customerList', 'customerCreate', 'customer360',
      'productList',
      'mySalesPipeline', 'salesAnalysis',
      'contractList', 'contractCreate', 'contractDetail', // ⭐ v363 合同 (sales 主用)
      'complaintsCenter', 'complaintCreate', 'complaintDetail',  // ⭐ v323 sales 报客诉 + 跟单
      'returnsList', 'returnCreate',                              // ⭐ v323 sales 看退货 + 创建
      'notifications', 'helpCenter',
    ],
  },

  engineer: {
    pages: [
      'engineerHome',
      'bomTreeEditor', 'productList', 'productCreate',
      'processManage', 'kitBomManage', 'artworkReviewQueue',
      'altMaterialList', 'corrugatedRecipeList',
      'cadLibrary', 'cadUpload',
      'orderMaterials', 'markCodeManage',           // ⭐ v350 算料 + 唛头
      'qualityGate', 'qcRecords',
      'notifications', 'helpCenter',
    ],
  },

  supervisor: {
    pages: [
      'supervisorHome',
      'ganttSchedule', 'productionKanban',
      'woManage', 'woTracking', 'woEdit', 'woCreate', 'woDetail', // ⭐ Sprint 3 WO 全
      'shortageList', // ⭐ v364 I-C 缺数+退货+补数中心
      'ipqcInline', 'qcRecords',
      'workshopScreen', 'oeeCenter',
      'bigScreenProduction', 'bigScreenData', // ⭐ v357 大屏
      'notifications', 'helpCenter',
    ],
  },

  worker: {
    // 极简 7 入口 (复用 v200.16.1)
    pages: [
      'workerHome',
      'scanStart', 'scanReport',
      'myWages', 'workerAttendance', 'workerRepair', 'workerDashboard',
      'notifications', 'helpCenter',
    ],
  },

  quality: {
    pages: [
      'qualityHome',
      'iqcIncoming', 'ipqcInline', 'oqcOutgoing',
      'qcRecords', 'qcDetail', 'qcDashboard', // ⭐ v362 J-2 QC 报告打印
      'complaintsCenter', 'complaintCreate', 'complaintDetail',  // ⭐ v323 客诉
      'returnsList', 'returnCreate',                              // ⭐ v323 RMA 退货
      'qualityGate',
      'notifications', 'helpCenter',
    ],
  },

  warehouse: {
    pages: [
      'warehouseHome', 'warehouseRawHome', 'warehouseFinishedHome', // ⭐ Sprint 3 子角色 home
      'receiveMaterial', 'receiptQR', 'issueMaterial',
      'inventoryList', 'inventoryAdjust', 'inventoryTransfer',
      'rawMaterialQuery', 'mrpDashboard',
      'logisticsCenter', 'stockByWarehouse',
      'finishedProductInventory', 'finishedProductList',
      'shippingNote', 'shippingNoteDetail', // ⭐ Sprint 4 发货 + v362 J-2 详情打印
      'oqcOutgoing', // OQC 出厂 (跨品控 · 成品仓 OQC 也用)
      'notifications', 'helpCenter',
    ],
  },

  maintenance: {
    pages: [
      'maintHome',
      'maintenanceList', 'maintenanceRequest',
      'equipmentList', 'sparePartsManage', 'equipmentByDept', // ⭐ v352 真设备清单
      'maintenancePlans', 'maintenanceSchedule',
      'tpmCenter',
      'notifications', 'helpCenter',
    ],
  },

  finance: {
    pages: [
      'financeHome',
      'customerPaymentCreate', 'customerPayments', 'paymentDetail', // ⭐ v362 J-2 收款凭证打印
      'contractList', 'contractDetail', // ⭐ v363 财务看合同付款条件
      'arInvoiceList', 'arInvoiceCreate', 'arInvoiceDetail', 'arAging', // ⭐ Sprint 3 ARCreate
      'apInvoiceList', 'apInvoiceCreate', 'apInvoiceDetail', 'apAging', 'financePipeline', // ⭐ v362 J-2 AP 存档打印
      'profitLoss', 'monthlyProfitReport', 'monthlyOverhead',
      'orderPnl', 'orderPnlReport', // ⭐ v365 I-D 订单损益 + 期间报告
      'voucherCreate', 'voucherList',
      'bankAccounts', 'reconciliation',
      'expenseHRReview', 'expenseFinanceReview',
      'payrollCalc', // 财务也看工资
      'complaintsCenter', 'returnsList', // ⭐ v325 财务关联退款/客诉
      'customer360', // ⭐ v325 财务看客户付款全景
      'notifications', 'helpCenter',
    ],
  },

  hr: {
    pages: [
      'hrHome',
      'employeeManage', 'employeeCreate', 'employeeDetail',
      'hrTimeCenter', 'payrollCalc',
      'leaveRequestList', 'overtimeRequestList', 'trainingRecordList',
      'expenseHRReview',
      'notifications', 'helpCenter',
    ],
  },

  purchasing: {
    pages: [
      'purchasingHome',
      'prList', 'prCreate', 'prDetail', 'rfqList', // ⭐ Sprint 3 prDetail + create
      'poList', 'poCreate', 'poDetail',
      'supplierList', 'supplierCreate', 'supplier360',
      'receiveMaterial', 'purchasingReport',
      'notifications', 'helpCenter',
    ],
  },

  guard: {
    // 极简 5 入口
    pages: [
      'guardHome',
      'guardScanIn', 'guardScanOut',
      'guardPatrolScan', 'guardPatrolDashboard',
      'guardHistory', 'guardGateControl',
      'notifications', 'helpCenter',
    ],
  },

  iso: {
    pages: [
      'isoHome',
      'qcRecords', 'qualityGate', 'complaintsCenter',
      'trainingRecordList',
      'iqcIncoming', 'oqcOutgoing',
      'notifications', 'helpCenter',
    ],
  },
};

// ============================================================
// 公开 API
// ============================================================

/**
 * 检查角色是否能访问页面 · router.ts 主调
 */
export function canAccess(user: User, page: string): boolean {
  const perm = ROLE_PERMISSIONS[user.role];
  if (!perm) return false; // 未知角色 拒绝

  // admin / gm / boss 全开
  if (perm._all) return true;

  // 白名单
  if (perm.pages?.includes(page)) return true;

  // 通用页 (所有角色都可访问)
  const universalPages = ['login', 'logout', 'profile', 'changePassword'];
  if (universalPages.includes(page)) return true;

  return false;
}

/**
 * 获取角色允许的所有页面 · sidebar 渲染用
 */
export function getRolePages(role: string): string[] {
  const perm = ROLE_PERMISSIONS[role];
  if (!perm) return [];
  if (perm._all) {
    // 全部页 (展开所有 role 的 union)
    const all = new Set<string>();
    for (const p of Object.values(ROLE_PERMISSIONS)) {
      p.pages?.forEach((page) => all.add(page));
    }
    return Array.from(all);
  }
  return perm.pages || [];
}

/**
 * 检查 HR sub-role (复用 v200.16.1 detectHrSubRole 设计)
 *
 * HR-01 = manager (16 页全开)
 * HR-02 = payroll (5 页 · 不看员工增删改)
 */
export function getHrSubRole(user: User): 'manager' | 'payroll' | null {
  if (user.role !== 'hr') return null;
  if (user.code.includes('PAY') || user.code === 'HR-02') return 'payroll';
  return 'manager';
}
